<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="row g-2 align-items-center mb-3">
      <div class="col-12 col-md-6">
        <select class="form-select form-select-sm" v-model="categoryFilter" @change="fetchJobTitles">
          <option value="">{{ $t('jobTitles.allCategories') }}</option>
          <option value="employee">{{ $t('jobTitles.employee') }}</option>
          <option value="teacher">{{ $t('jobTitles.teacher') }}</option>
        </select>
      </div>
      <div class="col-12 col-md-6 d-grid d-md-block text-md-end">
        <button class="btn btn-secondary" @click="openCreateDialog" v-can="'job_titles.create'">
          <i class="fas fa-plus me-2"></i>{{ $t('jobTitles.addNew') }}
        </button>
      </div>
    </div>

    <div class="card mb-3 mb-xxl-5" style="margin-top: 2.5rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('jobTitles.title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner v-if="isLoading" :message="$t('common.loading')" class="py-5" />
          <table v-else class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('jobTitles.name') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('jobTitles.category') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('jobTitles.status') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="jobTitles.length === 0">
                <td colspan="4" class="text-center py-4 text-muted">{{ $t('jobTitles.noResults') }}</td>
              </tr>
              <tr v-for="jt in jobTitles" :key="jt.id">
                <td><span class="text-sm">{{ jt.name }}</span></td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="jt.category === 'teacher' ? 'bg-info' : 'bg-secondary'">
                    {{ jt.category === 'teacher' ? $t('jobTitles.teacher') : $t('jobTitles.employee') }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="jt.is_active ? 'bg-success' : 'bg-danger'">
                    {{ jt.is_active ? $t('jobTitles.active') : $t('jobTitles.inactive') }}
                  </span>
                </td>
                <td class="text-center pe-4">
                  <button class="btn btn-link text-secondary p-1 mx-1" @click="openEditDialog(jt)" v-can="'job_titles.update'">
                    <i class="fas fa-edit fa-sm"></i>
                  </button>
                  <button class="btn btn-link text-danger p-1 mx-1" @click="openDeleteDialog(jt)" v-can="'job_titles.delete'">
                    <i class="fas fa-trash fa-sm"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <BaseDialog
      ref="formDialog"
      modal-id="jobTitleFormDialog"
      :title="editingId ? $t('jobTitles.editTitle') : $t('jobTitles.addTitle')"
      :confirm-text="$t('common.save')"
      :loading="submitting"
      @confirm="handleSave"
    >
      <template #body>
        <div class="mb-3">
          <label class="form-label">{{ $t('jobTitles.name') }} *</label>
          <input type="text" class="form-control" v-model.trim="form.name" :class="{ 'is-invalid': errors.name }" />
          <div class="invalid-feedback">{{ errors.name }}</div>
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('jobTitles.category') }} *</label>
          <select class="form-select" v-model="form.category" :class="{ 'is-invalid': errors.category }">
            <option value="employee">{{ $t('jobTitles.employee') }}</option>
            <option value="teacher">{{ $t('jobTitles.teacher') }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.category }}</div>
        </div>
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" v-model="form.is_active" id="jobTitleActive" />
          <label class="form-check-label" for="jobTitleActive">{{ $t('jobTitles.active') }}</label>
        </div>
      </template>
    </BaseDialog>

    <DeleteDialog
      ref="deleteDialog"
      :entity-name="$t('jobTitles.entity')"
      :entity-label="$t('jobTitles.entity')"
      @delete="handleDelete"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import BaseDialog from '@/components/common/BaseDialog.vue';
import DeleteDialog from '@/components/common/DeleteDialog.vue';

const { t } = useI18n();
const toast = useToast();

const jobTitles = ref([]);
const isLoading = ref(false);
const categoryFilter = ref('');

const formDialog = ref(null);
const deleteDialog = ref(null);
const submitting = ref(false);
const editingId = ref(null);
const errors = ref({});

const form = ref({ name: '', category: 'employee', is_active: true });

const fetchJobTitles = async () => {
  isLoading.value = true;
  try {
    const res = await api.get('/job-titles', { params: { all: 1, category: categoryFilter.value || undefined } });
    jobTitles.value = res.data.data || res.data || [];
  } catch (e) {
    toast.error(t('common.error'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateDialog = () => {
  editingId.value = null;
  form.value = { name: '', category: 'employee', is_active: true };
  errors.value = {};
  formDialog.value?.openDialog();
};

const openEditDialog = (jt) => {
  editingId.value = jt.id;
  form.value = { name: jt.name, category: jt.category, is_active: !!jt.is_active };
  errors.value = {};
  formDialog.value?.openDialog();
};


const handleSave = async () => {
  errors.value = {};
  if (!form.value.name) { errors.value.name = t('validation.required'); return; }

  submitting.value = true;
  try {
    if (editingId.value) {
      await api.put(`/job-titles/${editingId.value}`, form.value);
      toast.success(t('jobTitles.updated'));
    } else {
      await api.post('/job-titles', form.value);
      toast.success(t('jobTitles.created'));
    }
    formDialog.value?.closeDialog();
    await fetchJobTitles();
  } catch (e) {
    // Laravel validation errors come as { errors: { field: [msg] } }.
    // Our own business-rule guards (e.g. "in use, can't edit") return a
    // plain { message } with no `errors` key — those must go to a toast,
    // not be treated as per-field validation errors.
    if (e.response?.status === 422 && e.response?.data?.errors) {
      errors.value = Object.fromEntries(Object.entries(e.response.data.errors).map(([k, v]) => [k, v[0]]));
    } else {
      toast.error(e.response?.data?.message || t('common.error'));
    }
  } finally {
    submitting.value = false;
  }
};

const openDeleteDialog = (jt) => deleteDialog.value?.openDialog(jt.id, jt.name);

const handleDelete = async (id) => {
  try {
    await api.delete(`/job-titles/${id}`);
    toast.success(t('jobTitles.deleted'));
    await fetchJobTitles();
  } catch (e) {
    toast.error(e.response?.data?.message || t('common.error'));
  }
};

onMounted(fetchJobTitles);
</script>
